import { useState, useEffect } from 'react';
import logo from '../../../../src/assets/new-logo.png';

interface SidebarProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  darkMode: boolean;
}

export default function Sidebar({ activeSection, setActiveSection, darkMode }: SidebarProps) {
  const [expandedSection, setExpandedSection] = useState<string | null>('strategy');

  const mainSections: { id: string; name: string; icon: string; route: string }[] = [];

  const analyticsComingSoon = import.meta.env.VITE_ENABLE_ANALYTICS !== 'true';

  const strategyItems = [
    { id: 'social-presence-overview', name: 'Social Presence Overview', icon: 'ri-global-line', isComingSoon: analyticsComingSoon },
    { id: 'communication-direction', name: 'Communication Direction', icon: 'ri-chat-voice-line' },
    { id: 'audience-content-intelligence', name: 'Audience Intelligence', icon: 'ri-brain-line', isComingSoon: analyticsComingSoon },
    // { id: 'competitor-social-references', name: 'Competitor References', icon: 'ri-bar-chart-grouped-line' },
    { id: 'content-planning-engine', name: 'Content Planning Engine', icon: 'ri-calendar-todo-line' },
  ];

  const implementationItems = [
    { id: 'content-calendar-workspace', name: 'Content Calendar Workspace', icon: 'ri-calendar-event-line' },
    { id: 'comment-manager', name: 'Comments', icon: 'ri-chat-3-line' },
  ];

  const governanceItems = [
    { id: 'governance-dashboard', name: 'Governance Dashboard', icon: 'ri-dashboard-line' },
    { id: 'execution-governance-engine', name: 'Execution Governance Engine', icon: 'ri-task-line' },
    { id: 'performance-intelligence-engine', name: 'Performance Intelligence Engine', icon: 'ri-bar-chart-grouped-line', isComingSoon: analyticsComingSoon },
  ];

  const optimizationItems = [
    { id: 'performance-content-intelligence', name: 'Performance & Content Intelligence', icon: 'ri-brain-line', isComingSoon: analyticsComingSoon },
    { id: 'market-consumer-tg-intelligence', name: 'Market, Consumer & TG Intelligence', icon: 'ri-user-community-line' },
    { id: 'predictive-strategy-recalibration', name: 'Predictive Strategy Recalibration', icon: 'ri-refresh-line' },
  ];

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? '' : section);
  };

  // Open the group holding the active item when the section changes from
  // outside the sidebar (e.g. a notification deep link to the workspace).
  useEffect(() => {
    const groups: [string, { id: string }[]][] = [
      ['strategy', strategyItems],
      ['implementation', implementationItems],
      ['governance', governanceItems],
      ['optimization', optimizationItems],
    ];
    const owner = groups.find(([, items]) => items.some((i) => i.id === activeSection));
    if (owner) setExpandedSection(owner[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const isStrategyActive = strategyItems.some((i) => i.id === activeSection);
  const isImplementationActive = implementationItems.some((i) => i.id === activeSection);
  const isGovernanceActive = governanceItems.some((i) => i.id === activeSection);
  const isOptimizationActive = optimizationItems.some((i) => i.id === activeSection);

  return (
    <div className={`w-64 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-r h-screen flex flex-col flex-shrink-0`}>
      {/* Logo and Brand */}
      <div className="px-6 border-b border-gray-200 dark:border-gray-700 flex items-center h-[88px]">
        <img src={logo} alt="Marketing 4SIGHT Logo" className="h-14 w-auto object-contain" />
      </div>

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <div className="space-y-2">
          {/* Strategy Expandable Section */}
          <div>
            <button
              onClick={() => {
                toggleSection('strategy');
                if (!isStrategyActive) setActiveSection('communication-direction');
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left ${isStrategyActive
                ? darkMode
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-900'
                : darkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <div className="flex items-center gap-3">
                <i className="ri-lightbulb-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="font-medium text-sm tracking-wide">STRATEGY</span>
              </div>
              {expandedSection === 'strategy' ? (
                <i className="ri-arrow-up-s-line text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm"></i>
              )}
            </button>

            {expandedSection === 'strategy' && (
              <div className="mt-1 space-y-0.5">
                {strategyItems.map((item) => (
                  <div key={item.id} className="relative group w-full">
                    <button
                      onClick={() => {
                        if (!item.isComingSoon) {
                          setActiveSection(item.id);
                        }
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === item.id
                          ? 'bg-blue-600 text-white'
                          : item.isComingSoon
                          ? 'text-gray-400/70 opacity-60 cursor-not-allowed'
                          : darkMode
                          ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon && <i className={`${item.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>}
                        <span className="text-sm">{item.name}</span>
                      </div>
                      {item.isComingSoon && (
                        <div className="flex items-center">
                          <i className="ri-lock-line text-xs text-gray-400 group-hover:hidden"></i>
                          <span className="hidden group-hover:inline-block bg-gray-900 dark:bg-gray-800 text-white text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded shadow-sm">
                            Soon
                          </span>
                        </div>
                      )}
                    </button>
                    
                    {item.isComingSoon && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:flex flex-col items-center pointer-events-none z-50">
                        <div className="bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded shadow-md border border-gray-800 dark:border-gray-700 whitespace-nowrap">
                          Feature Coming Soon
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-900/95 dark:bg-gray-950/95 rotate-45 -mt-1 border-r border-b border-gray-800 dark:border-gray-700" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Implementation Expandable Section */}
          <div>
            <button
              onClick={() => {
                toggleSection('implementation');
                if (!isImplementationActive) setActiveSection('content-calendar-workspace');
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left ${isImplementationActive
                ? darkMode
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-900'
                : darkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <div className="flex items-center gap-3">
                <i className="ri-tools-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="font-medium text-sm tracking-wide">IMPLEMENTATION</span>
              </div>
              {expandedSection === 'implementation' ? (
                <i className="ri-arrow-up-s-line text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm"></i>
              )}
            </button>

            {expandedSection === 'implementation' && (
              <div className="mt-1 space-y-0.5">
                {implementationItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${activeSection === item.id
                      ? 'bg-blue-600 text-white'
                      : darkMode
                        ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                      }`}
                  >
                    {item.icon && <i className={`${item.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>}
                    <span className="text-sm">{item.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Governance Expandable Section */}
          <div>
            <button
              onClick={() => {
                toggleSection('governance');
                if (!isGovernanceActive) setActiveSection('governance-dashboard');
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left ${isGovernanceActive
                ? darkMode
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-900'
                : darkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <div className="flex items-center gap-3">
                <i className="ri-shield-check-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="font-medium text-sm tracking-wide">GOVERNANCE</span>
              </div>
              {expandedSection === 'governance' ? (
                <i className="ri-arrow-up-s-line text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm"></i>
              )}
            </button>

            {expandedSection === 'governance' && (
              <div className="mt-1 space-y-0.5">
                {governanceItems.map((item) => (
                  <div key={item.id} className="relative group w-full">
                    <button
                      onClick={() => {
                        if (!item.isComingSoon) {
                          setActiveSection(item.id);
                        }
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === item.id
                          ? 'bg-blue-600 text-white'
                          : item.isComingSoon
                          ? 'text-gray-400/70 opacity-60 cursor-not-allowed'
                          : darkMode
                          ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon && <i className={`${item.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>}
                        <span className="text-sm">{item.name}</span>
                      </div>
                      {item.isComingSoon && (
                        <div className="flex items-center">
                          <i className="ri-lock-line text-xs text-gray-400 group-hover:hidden"></i>
                          <span className="hidden group-hover:inline-block bg-gray-900 dark:bg-gray-800 text-white text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded shadow-sm">
                            Soon
                          </span>
                        </div>
                      )}
                    </button>

                    {item.isComingSoon && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:flex flex-col items-center pointer-events-none z-50">
                        <div className="bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded shadow-md border border-gray-800 dark:border-gray-700 whitespace-nowrap">
                          Feature Coming Soon
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-900/95 dark:bg-gray-950/95 rotate-45 -mt-1 border-r border-b border-gray-800 dark:border-gray-700" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Optimization Expandable Section */}
          <div>
            <button
              onClick={() => {
                toggleSection('optimization');
                if (!isOptimizationActive) setActiveSection('market-consumer-tg-intelligence');
              }}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors text-left ${isOptimizationActive
                ? darkMode
                  ? 'bg-gray-700 text-white'
                  : 'bg-gray-100 text-gray-900'
                : darkMode
                  ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                  : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <div className="flex items-center gap-3">
                <i className="ri-line-chart-line text-lg w-5 h-5 flex items-center justify-center"></i>
                <span className="font-medium text-sm tracking-wide">OPTIMIZATION</span>
              </div>
              {expandedSection === 'optimization' ? (
                <i className="ri-arrow-up-s-line text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm"></i>
              )}
            </button>

            {expandedSection === 'optimization' && (
              <div className="mt-1 space-y-0.5">
                {optimizationItems.map((item) => (
                  <div key={item.id} className="relative group w-full">
                    <button
                      onClick={() => {
                        if (!item.isComingSoon) {
                          setActiveSection(item.id);
                        }
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === item.id
                          ? 'bg-blue-600 text-white'
                          : item.isComingSoon
                          ? 'text-gray-400/70 opacity-60 cursor-not-allowed'
                          : darkMode
                          ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.icon && <i className={`${item.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>}
                        <span className="text-sm">{item.name}</span>
                      </div>
                      {item.isComingSoon && (
                        <div className="flex items-center">
                          <i className="ri-lock-line text-xs text-gray-400 group-hover:hidden"></i>
                          <span className="hidden group-hover:inline-block bg-gray-900 dark:bg-gray-800 text-white text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded shadow-sm">
                            Soon
                          </span>
                        </div>
                      )}
                    </button>

                    {item.isComingSoon && (
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:flex flex-col items-center pointer-events-none z-50">
                        <div className="bg-gray-900/95 dark:bg-gray-950/95 backdrop-blur-sm text-white text-[10px] font-bold px-2.5 py-1 rounded shadow-md border border-gray-800 dark:border-gray-700 whitespace-nowrap">
                          Feature Coming Soon
                        </div>
                        <div className="w-1.5 h-1.5 bg-gray-900/95 dark:bg-gray-950/95 rotate-45 -mt-1 border-r border-b border-gray-800 dark:border-gray-700" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Other Sections */}
          {mainSections.length > 0 && mainSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.route)}
              className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${activeSection === section.route
                ? 'bg-blue-600 text-white'
                : darkMode ? 'text-gray-400 hover:bg-gray-700 hover:text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <i className={`${section.icon} text-lg`}></i>
              <span className="font-semibold text-sm tracking-wide">{section.name}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Help Section */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
          <div className="flex items-center space-x-2 mb-2">
            <i className="ri-question-line text-blue-600"></i>
            <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Need Help?
            </span>
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            Get started with our quick guide
          </p>
          <button className="text-xs text-blue-600 hover:text-blue-700 font-medium whitespace-nowrap">
            View Tutorial &rarr;
          </button>
        </div>
      </div>
    </div>
  );
}